/**
 * StatusBreakdownBar — a stacked horizontal bar of minutes per visit status
 * (domain data colors, mirroring Status Board's per-status color coding).
 *
 * Fix Spec D: this bar always renders the FULL status breakdown regardless
 * of the page's current time lens — that IS the breakdown semantics, not a
 * gap to reconcile with the lens toggle (Session-3 review confirmed this is
 * intentional; keep it). Unknown status keys — present in the data, absent
 * from STATUS_META (e.g. a new workflow status added without a frontend
 * update) — collapse into one trailing gray "Other" segment instead of
 * being silently dropped from the bar or its total. Segment derivation and
 * the total (`Object.values(minutesByStatus)` — every key, known or not)
 * live in `../lib/statusBreakdown` as pure functions so this behavior is
 * unit-tested without rendering.
 */

import { formatMinutes } from '../types';
import { deriveStatusSegments, statusBreakdownTotal } from '../lib/statusBreakdown';

interface StatusBreakdownBarProps {
  minutesByStatus: Record<string, number>;
  /** Bar fills this share of its track (vs the page-level max), 0..1. */
  scale?: number;
  showLegend?: boolean;
}

export function StatusBreakdownBar({ minutesByStatus, scale = 1, showLegend = false }: StatusBreakdownBarProps) {
  const segments = deriveStatusSegments(minutesByStatus);
  const total = statusBreakdownTotal(minutesByStatus);
  if (total <= 0) {
    return <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700" aria-hidden="true" />;
  }
  const widthPct = Math.max(2, Math.min(100, scale * 100));

  return (
    <div>
      <div
        className="flex h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
        role="img"
        aria-label={segments.map((s) => `${s.label}: ${formatMinutes(s.minutes)}`).join(', ')}
      >
        <div className="flex h-full" style={{ width: `${widthPct}%` }}>
          {segments.map((s) => (
            <div
              key={s.key}
              className={s.barClass}
              style={{ width: `${(s.minutes / total) * 100}%` }}
              title={`${s.label}: ${formatMinutes(s.minutes)}`}
            />
          ))}
        </div>
      </div>
      {showLegend && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
          {segments.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <span className={`h-2 w-2 rounded-full ${s.barClass}`} aria-hidden="true" />
              {s.label} {formatMinutes(s.minutes)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default StatusBreakdownBar;
